import React, { useRef, useState, ReactNode, CSSProperties, useCallback, useId } from 'react'
import useResizeObserver from 'bwax-ui/hooks/useResizeObserver'
import clsx from 'clsx';

interface TabItem {
  key?: string | number;
  label: ReactNode;
  content: ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  tab?: string | number;
  defaultTab?: string | number;
  className?: string;
  tabContentClassName?: string;
  style?: CSSProperties;
  onTabChange?: (value: string) => void;
  noBorder?: boolean;
  size?: 'small' | 'normal' | 'large';
  navJustify?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around';
}

export default function Tabs_component({ 
  tabs, 
  defaultTab, 
  className, 
  tabContentClassName, 
  style, 
  tab,
  onTabChange, 
  noBorder = false,
  size = "normal",
  navJustify = "flex-start"
}: TabsProps) {
    const tabBarRef = useRef<HTMLDivElement>(null);
    const [tabBarHeight, setTabBarHeight] = useState<number>();
    const uniqueId = useId();

    // Determine the initial active tab
    const getInitialTab = () => {
        if (tab !== undefined) return tab.toString();
        if (defaultTab !== undefined) return defaultTab.toString();
        if (tabs && tabs.length > 0) {
            return (tabs[0].key !== undefined ? tabs[0].key : 0).toString();
        }
        return undefined;
    };

    const [internalTab, setInternalTab] = useState<string | undefined>(getInitialTab);

    // Use controlled value if provided, otherwise use internal state
    const activeTab = tab !== undefined ? tab.toString() : internalTab;

    useResizeObserver(tabBarRef, (entry) => {
        setTabBarHeight(entry.borderBoxSize[0].blockSize)
    });

    const handleTabClick = useCallback((tabValue: string) => {
        if (tab === undefined) {
            // Uncontrolled mode
            setInternalTab(tabValue);
        }
        onTabChange?.(tabValue);
    }, [tab, onTabChange]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent, currentIndex: number) => {
        let newIndex: number | null = null;
        
        if (e.key === 'ArrowRight') {
            newIndex = (currentIndex + 1) % tabs.length;
        } else if (e.key === 'ArrowLeft') {
            newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        } else if (e.key === 'Home') {
            newIndex = 0;
        } else if (e.key === 'End') {
            newIndex = tabs.length - 1;
        }

        if (newIndex !== null) {
            e.preventDefault();
            const newTab = tabs[newIndex];
            const newTabValue = (newTab.key !== undefined ? newTab.key : newIndex).toString();
            handleTabClick(newTabValue);
            // Focus the new tab
            const tabElement = document.getElementById(`${uniqueId}-tab-${newTabValue}`);
            tabElement?.focus();
        }
    }, [tabs, handleTabClick, uniqueId]);

    return (
        <div 
            className={clsx("flex flex-col", className)}
            style={{
                ...(tabBarHeight ? { "--tab-bar-height": `${tabBarHeight}px` } as React.CSSProperties : {}),
                ...style,
            }}
        >
            <div 
                role="tablist"
                className={clsx("shrink-0 flex border-[--mauve-4] flex-wrap", {
                    "border-b": !noBorder
                })} 
                ref={tabBarRef}
                style={{ justifyContent: navJustify }}
            >
                {
                    tabs.map(({ label, key }, index) => {
                        const tabValue = (key !== undefined ? key : index).toString();
                        const isActive = activeTab === tabValue;
                        return (
                            <button 
                                key={tabValue}
                                id={`${uniqueId}-tab-${tabValue}`}
                                role="tab"
                                type="button"
                                aria-selected={isActive}
                                aria-controls={`${uniqueId}-panel-${tabValue}`}
                                tabIndex={isActive ? 0 : -1}
                                onClick={() => handleTabClick(tabValue)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                className={clsx(
                                    "flex items-center justify-center flex-shrink-0 px-3 mx-1 my-1.5 rounded",
                                    "leading-none select-none",
                                    "hover:bg-[--main-3]",
                                    "focus:outline-none focus:relative",
                                    {
                                        "py-1 text-[13px]": size === "small",
                                        "py-2.5 text-[15px]": size === "normal",
                                        "py-4 text-[17px]": size === "large",
                                    },
                                    // Active state styles
                                    isActive ? [
                                        "text-[--main-11] relative",
                                        "after:content-[''] after:absolute after:bottom-[-6px]",
                                        "after:left-0 after:right-0 after:border-b-2",
                                        "after:border-current"
                                    ] : [
                                        "text-[--mauve-11]"
                                    ],
                                    "unstyled-focus"
                                )} 
                            >
                                {label}
                            </button>
                        )
                    })
                }
            </div>
            {
                tabs.map(({ content, key }, index) => {
                    const tabValue = (key !== undefined ? key : index).toString();
                    const isActive = activeTab === tabValue;
                    return (
                        <div 
                            key={tabValue}
                            id={`${uniqueId}-panel-${tabValue}`}
                            role="tabpanel"
                            aria-labelledby={`${uniqueId}-tab-${tabValue}`}
                            tabIndex={0}
                            hidden={!isActive}
                            className={clsx(
                                "flex-grow p-5 px-4 overflow-auto outline-none", 
                                "h-[calc(100%_-_var(--tab-bar-height))]",
                                tabContentClassName
                            )} 
                        >
                            {isActive && content}
                        </div>
                    )
                })
            }
        </div>
    )
}
